import { useContext, computed, useRoute, useRouter } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { noop } from 'lodash-es';
import { useKarteUserTag } from '~/composables/karte/useKarteUserTag';
import { useStore } from '~/composables/useStore';
import { COOKIE_KEY } from '~/constants';
import { karteTrack, url } from '~/utils';
import { UserCertification } from '~/types/common/user';
import { useEnotecaApp } from '../useEnotecaApp';

export const useAuthentication = () => {
  const { $cookies, app } = useContext();
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const { loginEnotecaApp, isEnotecaAppWebView } = useEnotecaApp();
  const karte = useKarteUserTag();

  /** ログイン状態か */
  const isAuthenticated = computed(() => store.state.auth.authenticated);

  /**
   * ログイン後の各種処理
   */
  const postLogin = async (
    returnTo = url('TOP'),
    userCertification?: UserCertification | null,
    lineMemberId?: string | null
  ): Promise<void> => {
    $cookies.remove(COOKIE_KEY.RETURN_TO);

    await store.dispatch('modules/cart/loginCart', {}).catch(noop);

    store.dispatch('modules/cart/clearGuestToken');
    await store.dispatch('user/info/fetchUserInfo', {});

    // LOOPASSタグ用にハッシュ化したUUIDをCookieに保存
    try {
      const { hashes } = await store.dispatch('user/user/encryptUuid', {
        body: {
          data: store.state.user.info.userBase?.uuid || '',
        },
      });
      if (hashes) {
        $cookies.set(COOKIE_KEY.UUID_HASH_NEW, hashes[0], {
          sameSite: 'none',
          secure: true,
          expires: dayjs().add(5, 'year').endOf('year').toDate(),
        });
      }
    } catch {
      // 何もしない
    }

    if (isEnotecaAppWebView.value) {
      await appPostLogin(userCertification);
      return;
    }

    // KARTE
    karteTrack('login', { status: true });
    await karte.setKarteUserTag(true, lineMemberId);

    /* GTMにタグ設置のイベントを発火 */
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'tlb_dmp_login',
      tlb_dmp: { user_id: store.state.user.info.userBase?.uuid },
    });

    // ここでソーシャルプラスへ遷移
    window.location.replace(returnTo);
  };

  // アプリのpostLogin処理
  const appPostLogin = async (userCertification?: UserCertification | null) => {
    const { flag, userBase } = store.state.user.info;
    app.$logger.send({
      method: 'WebView on postLogin()',
      url: route.value.fullPath,
      status: JSON.stringify({ flag, cardNo: userBase?.cardNo }),
      errorCode: `LINE/App (${app.$ua.original()})`,
    });

    const isSignUp = route.value.path === url('SIGN_UP');

    // 認証後のアプリ会員番号
    let afterAppCardNo = userBase?.cardNo;

    if (flag) {
      const { isAppLink, isGod, isDm, isCancelOften, uuid } = flag;

      // アプリ連携フラグが立っていなければ設定する
      if (!isAppLink) {
        await store.dispatch('user/info/updateFlag', {
          body: {
            isAppLink: true,
            isGod,
            isDm,
            isCancelOften,
            // @ts-expect-error
            uuid,
          },
        });
      }
    }

    // 仮会員合算
    // Cookie の自動パースが 0 から始まるときは string、7 などから始まるときは number になるので toString() している
    const appMemberCode = $cookies.get<string | number | undefined>(COOKIE_KEY.APP_MEMBER_CODE)?.toString();

    app.$logger.send({
      method: 'before point/addup on postLogin()',
      url: route.value.fullPath,
      status: JSON.stringify({ appMemberCode, cardNo: userBase?.cardNo }),
      errorCode: `LINE/App (${app.$ua.original()})`,
    });

    // 会員番号がアプリ仮会員番号とことなる場合にのみ合算を実施
    if (appMemberCode && userBase && userBase.cardNo !== appMemberCode) {
      afterAppCardNo = appMemberCode;

      app.$logger.send({
        method: 'point/addup payload',
        url: route.value.fullPath,
        status: JSON.stringify({
          beforeCardNo: userBase.cardNo,
          afterCardNo: appMemberCode,
        }),
        errorCode: `LINE/App (${app.$ua.original()})`,
      });

      await store.dispatch('modules/mypage/point/pointAddup', {
        body: {
          beforeCardNo: userBase.cardNo,
          afterCardNo: appMemberCode,
        },
      });

      $cookies.remove(COOKIE_KEY.APP_MEMBER_CODE);
    }

    // KARTEのloginイベント
    karteTrack('login', { status: true });
    await karte.setKarteUserTag(true, afterAppCardNo);

    /* GTMにタグ設置のイベントを発火 */
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'tlb_dmp_login',
      tlb_dmp: { user_id: userBase?.uuid },
    });

    if (route.value.path === url('AUTH_CB_SUCCESS')) {
      await router.replace(url('AUTH_LOGIN'));
    }

    // KARTEのsignupイベント
    if (isSignUp) {
      karteTrack('signup');
      await karte.setKarteUserTag(true, afterAppCardNo);
    }

    loginEnotecaApp(isSignUp ? 'signup' : 'login', userCertification, afterAppCardNo);
  };

  return {
    isAuthenticated,
    postLogin,
  };
};
